import { useEffect, useMemo, useState } from 'react';
import JobRichTextEditor from '../components/admin/JobRichTextEditor';
import {
  createAdminJob,
  deleteAdminJob,
  getAdminJobCategories,
  getAdminJobs,
  updateAdminJob,
  updateAdminJobStatus,
} from '../services/adminService';
import { legacyBlocksToHtml, sanitizeJobHtml } from '../utils/jobHtml';

const emptyJobForm = {
  title: '',
  slug: '',
  category: {
    name: '',
    slug: '',
    description: '',
  },
  status: 'draft',
  shortSummary: '',
  contentHtml: '',
  metadata: [{ label: 'Experience', value: '' }],
  contentBlocks: [],
  applyUrl: '/apply',
  order: 0,
};

const metadataLabels = [
  'Experience',
  'Age',
  'Work Type',
  'Salary',
  'Location',
  'Qualification',
  'Language',
  'Joining',
  'Department',
  'Job Type',
  'Openings',
];

function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '');
}

function cloneJobForm(job = emptyJobForm) {
  const legacyHtml = !job.contentHtml && job.contentBlocks?.length ? legacyBlocksToHtml(job.contentBlocks) : '';

  return {
    ...emptyJobForm,
    ...job,
    category: {
      ...emptyJobForm.category,
      ...(job.category || {}),
    },
    metadata: job.metadata?.length ? job.metadata.map((item) => ({ ...item })) : [],
    contentHtml: sanitizeJobHtml(job.contentHtml || legacyHtml || ''),
    contentBlocks: job.contentBlocks?.length
      ? job.contentBlocks.map((block) => ({ ...block, items: block.items || [] }))
      : [],
  };
}

function formatLabel(value) {
  return String(value || '')
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function AdminJobs() {
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(cloneJobForm());
  const [editingId, setEditingId] = useState('');
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [categorySlugTouched, setCategorySlugTouched] = useState(false);
  const [metadataLabelToAdd, setMetadataLabelToAdd] = useState('');

  const groupedJobs = useMemo(() => {
    const groups = new Map();

    jobs.forEach((job) => {
      const key = job.category?.slug || 'uncategorized';
      if (!groups.has(key)) {
        groups.set(key, {
          category: job.category || { name: 'Uncategorized', slug: key },
          jobs: [],
        });
      }

      groups.get(key).jobs.push(job);
    });

    return Array.from(groups.values());
  }, [jobs]);

  const loadJobs = async () => {
    const [jobsResult, categoriesResult] = await Promise.all([
      getAdminJobs(),
      getAdminJobCategories(),
    ]);

    setJobs(jobsResult.jobs || []);
    setCategories(categoriesResult.categories || []);
  };

  useEffect(() => {
    loadJobs()
      .then(() => setLoading(false))
      .catch(() => {
        setLoading(false);
        setFeedback({ type: 'error', message: 'Unable to load jobs.' });
      });
  }, []);

  const updateField = (name, value) => {
    setForm((current) => {
      const next = { ...current, [name]: value };

      if (name === 'title' && !slugTouched) {
        next.slug = slugify(value);
      }

      return next;
    });
  };

  const updateCategory = (name, value) => {
    setForm((current) => {
      const category = { ...current.category, [name]: value };

      if (name === 'name' && !categorySlugTouched) {
        category.slug = slugify(value);
      }

      return { ...current, category };
    });
  };

  const selectExistingCategory = (event) => {
    const selected = categories.find((category) => category.slug === event.target.value);

    if (!selected) {
      return;
    }

    setForm((current) => ({
      ...current,
      category: {
        name: selected.name,
        slug: selected.slug,
        description: selected.description || '',
      },
    }));
    setCategorySlugTouched(true);
  };

  const updateMetadata = (index, field, value) => {
    setForm((current) => ({
      ...current,
      metadata: current.metadata.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const addMetadataRow = () => {
    setForm((current) => ({
      ...current,
      metadata: [...current.metadata, { label: '', value: '' }],
    }));
  };

  const addPrebuiltMetadataRow = () => {
    if (!metadataLabelToAdd) {
      return;
    }

    setForm((current) => ({
      ...current,
      metadata: [...current.metadata, { label: metadataLabelToAdd, value: '' }],
    }));
    setMetadataLabelToAdd('');
  };

  const removeMetadataRow = (index) => {
    setForm((current) => ({
      ...current,
      metadata: current.metadata.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const updateContentHtml = (contentHtml) => {
    setForm((current) => ({
      ...current,
      contentHtml,
    }));
  };

  const selectJob = (job) => {
    setEditingId(job._id);
    setForm(cloneJobForm(job));
    setSlugTouched(true);
    setCategorySlugTouched(true);
    setFeedback({ type: '', message: '' });
  };

  const resetForm = () => {
    setEditingId('');
    setForm(cloneJobForm());
    setSlugTouched(false);
    setCategorySlugTouched(false);
    setFeedback({ type: '', message: '' });
  };

  const buildPayload = () => ({
    ...form,
    order: Number(form.order) || 0,
    metadata: form.metadata.filter((item) => item.label.trim() && item.value.trim()),
    contentHtml: sanitizeJobHtml(form.contentHtml || ''),
  });

  const saveJob = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFeedback({ type: '', message: '' });

    try {
      const payload = buildPayload();
      const result = editingId ? await updateAdminJob(editingId, payload) : await createAdminJob(payload);
      await loadJobs();
      setEditingId(result.job._id);
      setForm(cloneJobForm(result.job));
      setSlugTouched(true);
      setCategorySlugTouched(true);
      setFeedback({ type: 'success', message: 'Job saved.' });
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Unable to save job.' });
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (job) => {
    const nextStatus = job.status === 'published' ? 'draft' : 'published';

    try {
      const result = await updateAdminJobStatus(job._id, nextStatus);
      setJobs((current) => current.map((item) => (item._id === job._id ? result.job : item)));
      if (editingId === job._id) {
        setForm(cloneJobForm(result.job));
      }
    } catch (error) {
      setFeedback({ type: 'error', message: 'Unable to update status.' });
    }
  };

  const removeJob = async (job) => {
    if (!window.confirm(`Delete "${job.title}"?`)) {
      return;
    }

    try {
      await deleteAdminJob(job._id);
      setJobs((current) => current.filter((item) => item._id !== job._id));
      if (editingId === job._id) {
        resetForm();
      }
      setFeedback({ type: 'success', message: 'Job deleted.' });
    } catch (error) {
      setFeedback({ type: 'error', message: 'Unable to delete job.' });
    }
  };

  return (
    <section className="admin-page admin-jobs-page">
      <div className="admin-page__header admin-page__header--split">
        <div>
          <p className="eyebrow">Jobs CMS</p>
          <h1>Current openings</h1>
        </div>
        <button className="btn btn--secondary" onClick={resetForm} type="button">
          New Job
        </button>
      </div>

      {feedback.message ? (
        <p className={`form-alert form-alert--${feedback.type === 'error' ? 'error' : 'success'}`}>
          {feedback.message}
        </p>
      ) : null}

      <div className="admin-jobs-layout">
        <aside className="admin-panel admin-jobs-list-panel">
          <h2>Jobs</h2>
          {loading ? <p className="admin-empty">Loading jobs...</p> : null}
          {!loading && jobs.length === 0 ? <p className="admin-empty">No jobs created yet.</p> : null}
          <div className="admin-jobs-list">
            {groupedJobs.map((group) => (
              <div className="admin-job-group" key={group.category.slug}>
                <span>{group.category.name}</span>
                {group.jobs.map((job) => (
                  <article className="admin-job-row" key={job._id}>
                    <button onClick={() => selectJob(job)} type="button">
                      <strong>{job.title}</strong>
                      <small>
                        {formatLabel(job.status)} / {job.slug}
                      </small>
                    </button>
                    <div>
                      <button onClick={() => toggleStatus(job)} type="button">
                        {job.status === 'published' ? 'Draft' : 'Publish'}
                      </button>
                      <button onClick={() => removeJob(job)} type="button">
                        Delete
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            ))}
          </div>
        </aside>

        <form className="admin-panel admin-job-editor" onSubmit={saveJob}>
          <div className="admin-job-editor__section">
            <h2>{editingId ? 'Edit job post' : 'Add job post'}</h2>
            <div className="admin-form-grid">
              <label>
                Job title
                <input
                  onChange={(event) => updateField('title', event.target.value)}
                  required
                  value={form.title}
                />
              </label>
              <label>
                Job slug
                <input
                  onChange={(event) => {
                    setSlugTouched(true);
                    updateField('slug', slugify(event.target.value));
                  }}
                  placeholder="auto-generated"
                  value={form.slug}
                />
              </label>
              <label>
                Status
                <select onChange={(event) => updateField('status', event.target.value)} value={form.status}>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </label>
              <label>
                Sort order
                <input
                  min="0"
                  onChange={(event) => updateField('order', event.target.value)}
                  type="number"
                  value={form.order}
                />
              </label>
            </div>

            <label>
              Short summary
              <textarea
                onChange={(event) => updateField('shortSummary', event.target.value)}
                rows="3"
                value={form.shortSummary}
              />
            </label>
          </div>

          <div className="admin-job-editor__section">
            <div className="admin-job-editor__section-header">
              <h2>Category</h2>
              <select onChange={selectExistingCategory} value="">
                <option value="">Use existing category</option>
                {categories.map((category) => (
                  <option key={category.slug} value={category.slug}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="admin-form-grid">
              <label>
                Category name
                <input
                  onChange={(event) => updateCategory('name', event.target.value)}
                  required
                  value={form.category.name}
                />
              </label>
              <label>
                Category slug
                <input
                  onChange={(event) => {
                    setCategorySlugTouched(true);
                    updateCategory('slug', slugify(event.target.value));
                  }}
                  value={form.category.slug}
                />
              </label>
            </div>
            <label>
              Category description
              <textarea
                onChange={(event) => updateCategory('description', event.target.value)}
                rows="2"
                value={form.category.description}
              />
            </label>
          </div>

          <div className="admin-job-editor__section">
            <div className="admin-job-editor__section-header">
              <h2>Metadata rows</h2>
              <div className="admin-metadata-actions">
                <select
                  onChange={(event) => setMetadataLabelToAdd(event.target.value)}
                  value={metadataLabelToAdd}
                >
                  <option value="">Select metadata label</option>
                  {metadataLabels.map((label) => (
                    <option key={label} value={label}>
                      {label}
                    </option>
                  ))}
                </select>
                <button className="btn btn--secondary" onClick={addPrebuiltMetadataRow} type="button">
                  Add Metadata
                </button>
                <button className="btn btn--secondary" onClick={addMetadataRow} type="button">
                  Add Custom Metadata
                </button>
              </div>
            </div>
            <div className="admin-repeat-list">
              {form.metadata.map((item, index) => (
                <div className="admin-repeat-row" key={`metadata-${index}`}>
                  <input
                    onChange={(event) => updateMetadata(index, 'label', event.target.value)}
                    placeholder="Label"
                    value={item.label}
                  />
                  <input
                    onChange={(event) => updateMetadata(index, 'value', event.target.value)}
                    placeholder="Value"
                    value={item.value}
                  />
                  <button onClick={() => removeMetadataRow(index)} type="button">
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="admin-job-editor__section">
            <h2>Job description</h2>
            <JobRichTextEditor onChange={updateContentHtml} value={form.contentHtml} />
          </div>

          <div className="admin-job-editor__section">
            <label>
              Apply URL
              <input
                onChange={(event) => updateField('applyUrl', event.target.value)}
                placeholder="/apply"
                value={form.applyUrl}
              />
            </label>
          </div>

          <div className="admin-job-editor__actions">
            <button className="btn btn--primary" disabled={saving} type="submit">
              {saving ? 'Saving...' : 'Save Job'}
            </button>
            <button className="btn btn--secondary" onClick={resetForm} type="button">
              Clear
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

export default AdminJobs;
